// Application RBAC Configuration
// This file aggregates permissions from all features and defines roles

// Import feature-specific permissions
import { AUTH_PERMISSIONS } from '@/features/auth/constants';
import { CUSTOMERS_PERMISSIONS } from './customers/constants/permissions.constants';
import { DASHBOARD_PERMISSIONS } from './dashboard/constants/permissions.constants';
import { USER_PERMISSIONS } from './users/constants';

// Aggregate all permissions
export const PERMISSIONS = {
  ...AUTH_PERMISSIONS,
  ...CUSTOMERS_PERMISSIONS,
  ...DASHBOARD_PERMISSIONS,
  ...USER_PERMISSIONS,
} as const;

// Define application roles
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  /** Creates/edits Power Admins scoped to those they created; tenant ops like Power Admin. */
  TENANT_ADMIN: 'TENANT_ADMIN',
  POWER_ADMIN: 'POWER_ADMIN',
  NORMAL_USER: 'NORMAL_USER',
} as const;

// Role-permission mapping
export const ROLE_PERMISSIONS = {
  [ROLES.SUPER_ADMIN]: [
    // Admin has all permissions
    ...Object.values(PERMISSIONS),
  ],

  [ROLES.TENANT_ADMIN]: [
    // Same operational scope as Power Admin; who can be managed is enforced in canManageUser + API
    // admin:dashboard — sidebar items keyed with `admin` (e.g. Notifications)
    PERMISSIONS.ADMIN_DASHBOARD,
    PERMISSIONS.PROFILE_READ,
    PERMISSIONS.PROFILE_UPDATE,
    PERMISSIONS.CUSTOMERS_READ,
    PERMISSIONS.CUSTOMERS_CREATE,
    PERMISSIONS.CUSTOMERS_UPDATE,
    PERMISSIONS.CUSTOMERS_REPORTS_VIEW,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.DASHBOARD_ANALYTICS_VIEW,
    PERMISSIONS.DASHBOARD_SALES_VIEW,
    PERMISSIONS.DASHBOARD_TRAFFIC_VIEW,
    PERMISSIONS.DASHBOARD_REPORTS_VIEW,
    PERMISSIONS.SETTINGS_READ,
    PERMISSIONS.SETTINGS_UPDATE,
    PERMISSIONS.USERS_READ,
    PERMISSIONS.USERS_CREATE,
    PERMISSIONS.USERS_UPDATE,
    PERMISSIONS.USERS_DELETE,
    /** Route guards on /users use `user:*` (USER_PERMISSIONS), not `users:*`. */
    PERMISSIONS.USER_READ,
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_UPDATE,
    PERMISSIONS.USER_DELETE,
    PERMISSIONS.CUSTOMERS_CANCEL_SUBSCRIPTION,
  ],

  [ROLES.POWER_ADMIN]: [
    // Manager permissions - limited user management + content + customers + dashboard + settings
    PERMISSIONS.ADMIN_DASHBOARD,
    PERMISSIONS.PROFILE_READ,
    PERMISSIONS.PROFILE_UPDATE,
    PERMISSIONS.CUSTOMERS_READ,
    PERMISSIONS.CUSTOMERS_CREATE,
    PERMISSIONS.CUSTOMERS_UPDATE,
    PERMISSIONS.CUSTOMERS_REPORTS_VIEW,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.DASHBOARD_ANALYTICS_VIEW,
    PERMISSIONS.DASHBOARD_SALES_VIEW,
    PERMISSIONS.DASHBOARD_TRAFFIC_VIEW,
    PERMISSIONS.DASHBOARD_REPORTS_VIEW,
    PERMISSIONS.SETTINGS_READ,
    PERMISSIONS.SETTINGS_UPDATE,
    PERMISSIONS.USERS_READ,
    PERMISSIONS.USERS_CREATE,
    PERMISSIONS.USERS_UPDATE,
    PERMISSIONS.USERS_DELETE,
    PERMISSIONS.CUSTOMERS_CREATE,
    PERMISSIONS.CUSTOMERS_UPDATE,
    PERMISSIONS.CUSTOMERS_CANCEL_SUBSCRIPTION,
    PERMISSIONS.CUSTOMERS_READ,
    PERMISSIONS.USER_READ,
    PERMISSIONS.USER_CREATE,
    PERMISSIONS.USER_UPDATE,
    PERMISSIONS.USER_DELETE,
  ],

  [ROLES.NORMAL_USER]: [
    // Basic user permissions - profile + read content + basic dashboard + basic settings
    PERMISSIONS.PROFILE_READ,
    PERMISSIONS.PROFILE_UPDATE,
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.CUSTOMERS_READ,
    PERMISSIONS.SETTINGS_READ,
  ],
} as const;
